import { useState, useRef, useEffect, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

type SuggestionInputProps = Omit<React.ComponentProps<'input'>, 'onChange'> & {
  suggestions?: string[]
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onValueChange?: (value: string) => void
}

export function SuggestionInput({
  suggestions = [],
  value = '',
  onChange,
  onValueChange,
  className,
  ...props
}: SuggestionInputProps) {
  const [open, setOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const filtered = suggestions.filter((s) =>
    s.toLowerCase().includes((value as string).toLowerCase())
  )

  const showDropdown = open && filtered.length > 0

  const selectValue = useCallback(
    (val: string) => {
      if (onValueChange) {
        onValueChange(val)
      } else if (onChange) {
        const nativeEvent = new Event('input', { bubbles: true })
        const syntheticTarget = { value: val } as HTMLInputElement
        const syntheticEvent = {
          ...nativeEvent,
          target: syntheticTarget,
          currentTarget: syntheticTarget,
        } as unknown as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
      setOpen(false)
      setHighlightIndex(-1)
    },
    [onChange, onValueChange]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e)
    onValueChange?.(e.target.value)
    setOpen(true)
    setHighlightIndex(-1)
  }

  const handleFocus = () => {
    if ((value as string).length > 0 || suggestions.length > 0) {
      setOpen(true)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1))
        break
      case 'Enter':
        if (highlightIndex >= 0 && highlightIndex < filtered.length) {
          e.preventDefault()
          selectValue(filtered[highlightIndex])
        }
        break
      case 'Escape':
        setOpen(false)
        setHighlightIndex(-1)
        break
    }
  }

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightIndex])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        className={className}
        autoComplete="off"
        {...props}
      />
      {showDropdown && (
        <ul
          ref={listRef}
          className={cn(
            'absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md'
          )}
        >
          {filtered.map((item, index) => (
            <li
              key={item}
              className={cn(
                'cursor-pointer rounded-sm px-2 py-1.5 text-sm',
                index === highlightIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent hover:text-accent-foreground'
              )}
              onMouseDown={(e) => {
                e.preventDefault()
                selectValue(item)
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
